// 📱 ChauffeurAdresModal — stuurt leverings-info via WhatsApp naar chauffeur/vervoerder
// Ondersteunt zowel single (1 leverbon) als bulk-mode (meerdere leverbonnen samengevat).
// Bulk: stops worden chronologisch gesorteerd op gepland_leveringsdatum, genummerd 1️⃣ 2️⃣ ...
import React, { useEffect, useState, useMemo } from 'react';
import { X, WhatsappLogo, Copy, Truck, User, MapPin } from '@phosphor-icons/react';
import API from '@/lib/api';
import { toast } from 'sonner';

const _normaliseerGsm = (gsm) => {
  if (!gsm) return '';
  let n = (gsm + '').replace(/[^0-9+]/g, '');
  if (n.startsWith('00')) n = '+' + n.slice(2);
  if (n.startsWith('0') && !n.startsWith('+')) n = '+32' + n.slice(1);  // BE default
  return n.replace(/[^0-9]/g, '');
};

const _nummerEmojis = ['1️⃣', '2️⃣', '3️⃣', '4️⃣', '5️⃣', '6️⃣', '7️⃣', '8️⃣', '9️⃣', '🔟'];
const _stopNummer = (i) => _nummerEmojis[i] || `${i + 1}.`;

const _fmtDatum = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('nl-BE', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default function ChauffeurAdresModal({ leverbonId, leverbonIds, onClose }) {
  // Normaliseer naar array; backwards-compat met single leverbonId prop
  const ids = useMemo(() => {
    if (Array.isArray(leverbonIds) && leverbonIds.length > 0) return leverbonIds;
    if (leverbonId) return [leverbonId];
    return [];
  }, [leverbonId, leverbonIds]);
  const isBulk = ids.length > 1;

  const [leverbonnen, setLeverbonnen] = useState([]);
  const [klantenMap, setKlantenMap] = useState({}); // id → klant
  const [chauffeurs, setChauffeurs] = useState([]);
  const [chauffeurId, setChauffeurId] = useState('');
  const [tekst, setTekst] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Parallel: alle leverbonnen + chauffeurs + klanten
        const [lbResults, chR, kR] = await Promise.all([
          Promise.all(ids.map(id => API.get(`/documenten/${id}`).then(r => r.data).catch(() => null))),
          API.get('/chauffeurs'),
          API.get('/klanten'),
        ]);
        if (cancelled) return;
        const lbs = lbResults.filter(Boolean);
        // Sort op gepland_leveringsdatum (chronologisch), undefined achteraan
        lbs.sort((a, b) => {
          const da = a.gepland_leveringsdatum || '\uffff';
          const db = b.gepland_leveringsdatum || '\uffff';
          return da.localeCompare(db);
        });
        setLeverbonnen(lbs);
        setChauffeurs((chR.data || []).filter(c => c.actief !== false));
        // Map klanten per id
        const allKlanten = Array.isArray(kR.data) ? kR.data : (kR.data?.items || []);
        const km = {};
        allKlanten.forEach(k => { km[k.id] = k; });
        setKlantenMap(km);
        // Pre-select chauffeur: van eerste leverbon met chauffeur_id, of leeg
        const firstWithCh = lbs.find(l => l.chauffeur_id);
        if (firstWithCh) setChauffeurId(firstWithCh.chauffeur_id);
      } catch (e) {
        toast.error('Leverbon-details niet kunnen laden');
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [ids]);

  // Maak per-leverbon detail-blok
  const stopBlock = (lb, i) => {
    const k = klantenMap[lb.klant_id] || {};
    const klantNaam = k.bedrijf || k.naam || '(geen klant)';
    const adresParts = [
      k.adres,
      [k.postcode, k.stad].filter(Boolean).join(' '),
      k.land && k.land !== 'België' ? k.land : '',
    ].filter(Boolean);
    const adres = adresParts.join(', ');
    const tel = k.tel || k.gsm || '';
    const totaal = (lb.lijnen || []).reduce((s, l) => s + (parseFloat(l.aantal) || 0), 0);
    const stuks = Number.isInteger(totaal) ? totaal : totaal.toFixed(2);
    const prefix = isBulk ? `${_stopNummer(i)} ` : '';
    const lines = [`${prefix}🚚 ${lb.id} · ${klantNaam}`];
    if (adres) lines.push(`📍 ${adres}`);
    if (tel) lines.push(`📞 ${tel}`);
    if (stuks) lines.push(`📦 ${stuks} stuks`);
    if (lb.gepland_leveringsdatum) lines.push(`🕒 ${_fmtDatum(lb.gepland_leveringsdatum)}`);
    if (lb.nota && !isBulk) lines.push(`📝 ${lb.nota}`);
    return lines.join('\n');
  };

  // Genereer bericht automatisch
  useEffect(() => {
    if (loading || leverbonnen.length === 0 || Object.keys(klantenMap).length === 0) return;
    if (isBulk) {
      // Bulk: kop met datum + aantal stops, daarna genummerde blokken
      const eersteDatum = leverbonnen.find(l => l.gepland_leveringsdatum)?.gepland_leveringsdatum;
      const dagLabel = eersteDatum ? _fmtDatum(eersteDatum).split(' ').slice(0, 3).join(' ') : '';
      const kop = dagLabel
        ? `🚚 Route ${dagLabel} — ${leverbonnen.length} leveringen:`
        : `🚚 ${leverbonnen.length} leveringen:`;
      const blocks = leverbonnen.map((lb, i) => stopBlock(lb, i));
      setTekst([kop, '', ...blocks].join('\n\n'));
    } else if (leverbonnen[0]) {
      setTekst(stopBlock(leverbonnen[0], 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, leverbonnen, klantenMap, isBulk]);

  const huidigeChauffeur = chauffeurs.find(c => c.id === chauffeurId);
  const chauffeurTel = huidigeChauffeur?.tel || '';
  const gsmNormalized = _normaliseerGsm(chauffeurTel);
  const kanVersturen = !!gsmNormalized && !!tekst.trim();

  const openWhatsApp = () => {
    if (!kanVersturen) return;
    const url = `https://wa.me/${gsmNormalized}?text=${encodeURIComponent(tekst)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('WhatsApp geopend — controleer of de tekst correct is voor je verstuurt');
  };

  const kopieerTekst = async () => {
    try {
      await navigator.clipboard.writeText(tekst);
      toast.success('Tekst gekopieerd naar klembord');
    } catch {
      toast.error('Kopiëren mislukt');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="chauffeur-adres-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <WhatsappLogo size={22} weight="fill" />
            <div>
              <div className="font-bold text-sm">
                {isBulk ? `${leverbonnen.length} leveringen naar chauffeur` : 'Leveringsadres naar chauffeur'}
              </div>
              <div className="text-xs opacity-90">
                {isBulk ? 'Stuur alle stops in 1 WhatsApp-bericht' : 'Stuur de levering-info via WhatsApp'}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/20 rounded p-1" data-testid="chauffeur-modal-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 overflow-y-auto">
          {loading ? (
            <div className="text-center text-sm text-gray-500 py-8">Laden...</div>
          ) : (
            <>
              {/* Stops-samenvatting (alleen bij bulk of beknopt bij single) */}
              {isBulk ? (
                <div className="bg-gray-50 border border-gray-200 rounded p-2 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                  {leverbonnen.map((lb, i) => {
                    const k = klantenMap[lb.klant_id] || {};
                    return (
                      <div key={lb.id} className="flex items-center gap-2 py-0.5">
                        <span className="text-emerald-600 font-bold">{_stopNummer(i)}</span>
                        <span className="font-mono text-[10px] text-gray-500">{lb.id}</span>
                        <span className="font-semibold truncate">{k.bedrijf || k.naam || '?'}</span>
                        {k.stad && <span className="text-gray-400 text-[10px]">· {k.stad}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : leverbonnen[0] && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3 text-xs space-y-1">
                  <div className="flex items-center gap-1.5"><Truck size={12} className="text-emerald-600" /> <strong>{leverbonnen[0].id}</strong></div>
                  <div className="flex items-center gap-1.5">
                    <User size={12} className="text-gray-500" />
                    {(klantenMap[leverbonnen[0].klant_id]?.bedrijf) || (klantenMap[leverbonnen[0].klant_id]?.naam) || '(geen klant)'}
                  </div>
                </div>
              )}

              {/* Chauffeur-keuze */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Naar welke chauffeur?</label>
                <select
                  value={chauffeurId}
                  onChange={e => setChauffeurId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  data-testid="chauffeur-modal-select"
                >
                  <option value="">— Kies een chauffeur —</option>
                  {chauffeurs.map(c => (
                    <option key={c.id} value={c.id} disabled={!c.tel}>
                      {c.naam} {c.tel ? `· ${c.tel}` : '(geen tel)'}
                    </option>
                  ))}
                </select>
                {chauffeurId && !chauffeurTel && (
                  <div className="text-[10px] text-red-600 mt-1">⚠️ Deze chauffeur heeft geen telefoonnummer ingesteld.</div>
                )}
                {chauffeurId && chauffeurTel && (
                  <div className="text-[10px] text-emerald-700 mt-1">📞 {chauffeurTel} → wordt verstuurd via WhatsApp</div>
                )}
              </div>

              {/* Bericht editor */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bericht (aanpasbaar)</label>
                <textarea
                  value={tekst}
                  onChange={e => setTekst(e.target.value)}
                  rows={isBulk ? 14 : 10}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-xs font-mono whitespace-pre-wrap"
                  data-testid="chauffeur-modal-tekst"
                />
                <div className="text-[10px] text-gray-400 mt-1">
                  {tekst.length} tekens {isBulk && `· ${leverbonnen.length} stops`}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-3 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={kopieerTekst}
            disabled={!tekst}
            className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-100 border border-gray-300 rounded inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="chauffeur-modal-kopieer"
          >
            <Copy size={12} /> Kopieer
          </button>
          <button
            onClick={openWhatsApp}
            disabled={!kanVersturen}
            className="px-4 py-1.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 rounded inline-flex items-center gap-1.5"
            data-testid="chauffeur-modal-whatsapp"
          >
            <WhatsappLogo size={14} weight="fill" /> Open in WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}
